import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import SidebarSkeletonList from './sidebar_skeleton_list';
import vahoy from '../../../../javascript/vahoy';
import vapi from '../../../../javascript/frontend/api/vapi';
import Avatar from '../../../avatar/avatar';
import { CompanyContext } from '../../data/company_context_management';

function PrUserList({ previewMode }) {
  const {
    companyId,
    company,
    setPrUsersExist,
    setOpenEditModal,
  } = useContext(CompanyContext);
  const [isLoading, setIsLoading] = useState(true);
  const [prUsers, setPrUsers] = useState([]);
  const [asyncError, setAsyncError] = useState();

  // Retrieve the pr_users list from the API
  useEffect(() => {
    let componentIsMounted = true;
    const fetchPrUsers = async () => {
      try {
        if (companyId) {
          setIsLoading(true);
          vahoy.track('Companies#getPrUsers');

          const params = {
            company_id: companyId,
          };

          const response = await vapi.getPrUsers(params);
          if (componentIsMounted && response.status === 200) {
            const prUserResults = response.data;
            if (prUserResults.data) {
              if (previewMode) {
                // Limit to 3 records if we're in preview mode
                setPrUsers(prUserResults.data.slice(0, 3));
              } else {
                setPrUsers(prUserResults.data);
              }
            }
          }
          if (componentIsMounted) setIsLoading(false);
        }
      } catch (error) {
        setAsyncError(error);
      }
    };
    fetchPrUsers();

    // Cleanup function to avoid "Can't perform state update on unmounted component" error
    return () => {
      componentIsMounted = false;
    };
  }, [companyId, previewMode]);

  // Update company context to indicate PR user records exist
  useEffect(() => {
    setPrUsersExist(prUsers.length > 0);
  }, [prUsers, setPrUsersExist]);

  // Let top-level ErrorBoundary see any async errors
  if (asyncError) throw asyncError;

  if (!isLoading && prUsers.length === 0) return null;
  if (!company.show_pr_users && !previewMode) return null;

  return (
    <div>
      <div className="card mb-3 animated faster fadeIn">
        <div className="card-body pb-2">
          <div className="fw-bold mb-3 pb-2 text-uppercase small border-bottom d-flex align-items-center">
            Our PR Team
            {!previewMode && company.editable && (
              <button
                className="btn btn-primary btn-sm ms-auto"
                style={{
                  maxHeight: '2rem',
                  maxWidth: '2rem',
                }}
                onClick={() => setOpenEditModal('PR_USERS')}
                type="button"
              >
                <i className="fa-solid fa-pencil cursor-pointer" />
              </button>
            )}
          </div>
          {isLoading && (
            <SidebarSkeletonList numberOfLines={3} />
          )}
          {!isLoading && prUsers.map((prUser) => (
            <div className="d-flex ms-1 mb-2 align-items-center animated faster fadeIn" key={prUser.id}>
              <Avatar
                avatarUrl={prUser.attributes.avatar_url}
                avatarLetters={prUser.attributes.initials}
                sizeClass="avatar company-show-sidebar-expert-img"
              />
              <div className="flex-grow-1">
                <a href={prUser.attributes.pr_user_path} style={{ fontWeight: 'bold' }}>
                  {prUser.attributes.full_name}
                </a>
                <span className="text-secondary" style={{ display: 'block' }}>
                  {prUser.attributes.employments_string}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default PrUserList;

PrUserList.propTypes = {
  previewMode: PropTypes.bool,
};

PrUserList.defaultProps = {
  previewMode: false,
};
